import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {saleinvoiceGetAll, saleinvoiceExcel} from '../../../redux/slices/saleinvoice.slice'
import {categoryGetAll} from '../../../redux/slices/category.slice'
import {allCompanyLookup, projectLookup, basicLookup} from '../../../redux/slices/lookup.slice'

import {SalePaymentStatus} from '../../../helpers'

import {
  useMemoizedStaticData
} from '../../../utils/performanceOptimizations'

const selectSaleInvoiceData = (state) => ({
  saleinvoice: state.saleinvoice.saleinvoice,
  loading: state.saleinvoice.loading
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  InvoiceKind: state.lookup.InvoiceKind
})

const selectCategoryData = (state) => ({
  category: state.category.category
})

function List() {
  const intl = useIntl()
  const {saleinvoice, loading} = useSelector(selectSaleInvoiceData)
  const {projectLookupList, companyLookup, InvoiceKind} = useSelector(selectLookupData)
  const {category} = useSelector(selectCategoryData)
  const dispatch = useDispatch()

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(saleinvoiceGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(projectLookup())
    dispatch(basicLookup('InvoiceKind'))
    dispatch(categoryGetAll({SourceTypeId: 7}))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      saleinvoiceExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.SALESINVOICES'}),
      })
    )
  }, [dispatch, params, intl])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'SALEINVOICE.SEARCH.NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCellDouble item={props} double='companyName' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'SALEINVOICE.SEARCH.PROJECTNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'SALEINVOICE.SEARCH.CATEGORYNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'categoryName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'SALEINVOICE.SEARCH.INVOICEKINDNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'invoiceKindName',
      Cell: ({...props}) => (
        <CustomCell item={props} />
      ),
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'SALEINVOICE.SEARCH.EDITDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'editDate',
      Cell: ({...props}) => <CustomCell item={props} date='short' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'SALEINVOICE.SEARCH.DEFERMENTDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'defermentDate',
      Cell: ({...props}) => <CustomCellDouble item={props} date='short' double='delayDay' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'SALEINVOICE.SEARCH.TOTALPRICE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'totalPrice',
      Cell: ({...props}) => <CustomCell item={props} price />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'EXPENSEINVOICE.SEARCH.BALANCE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'balance',
      Cell: ({...props}) => <CustomCellDouble item={props} double='paymentStatusName' price />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'lastModifiedBy',
      Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
    },
  ], [intl, handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'SALEINVOICE.SEARCH.PROJECTNAME',
          data: projectLookupList,
          name: 'projectId',
        },
        {
          title: 'FILTER.SALEINVOICE.COMPANYID',
          data: companyLookup,
          name: 'companyId',
        },
        {
          title: 'FILTER.SALEINVOICE.SALEPAYMENTSTATUSID',
          data: SalePaymentStatus,
          name: 'salePaymentStatusId',
        },
        {
          title: 'SALEINVOICE.SEARCH.EDITSTARTDATE',
          type: 'date',
          name: 'editStartDate',
        },
        {
          title: 'SALEINVOICE.SEARCH.EDITENDDATE',
          type: 'date',
          name: 'editEndDate',
        },
        {
          title: 'SALEINVOICE.SEARCH.CATEGORYNAME',
          data: category,
          name: 'categoryId',
        },
        {
          title: 'SALEINVOICE.SEARCH.INVOICEKINDNAME',
          data: InvoiceKind,
          name: 'invoiceKindId',
        },
      ],
    },
  ], [handleExcelExport, projectLookupList, companyLookup, SalePaymentStatus, category, InvoiceKind])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.SALESINVOICES'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={'SALEINVOICES'}
        />
        <KTCardBody>
          {!saleinvoice && <ListLoading />}
          {saleinvoice && (
            <Table
              items={saleinvoice}
              cols={columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
